// ─── Public Status Page — subscribe ──────────────────────────────────
//
// The ONE stateful component on the page, and the only one that writes anything
// anywhere. It owns the whole flow — session → (captcha) → submit → SMS
// one-time code — because none of that state is of the slightest interest to the
// rest of the page, and App.tsx mounts it only while it is open.
//
// It reuses the app's own BottomSheet (apps/web/src/components/BottomSheet.tsx)
// rather than hand-rolling a modal. That component's imports were checked before
// adopting it: react/react-dom, lib/useMediaQuery, lib/breakpoints and
// lib/scrollLock — all leaf modules with no store, no lib/api.ts and therefore
// no chance of dragging the authenticated API client into this bundle. Its
// `.sheet-*` styles come from responsive.css, which status.css already imports.
//
// ── ONE FORM, BOTH CHANNELS ──
// There is no channel picker. The form asks for an email address AND a mobile
// number side by side and takes whichever are filled in, which is what the page
// this replaces does. A segmented control made the visitor answer a question
// they have no stake in ("which of our pipes would you like to use?") and then
// hid the other field behind it.
//
// Both fields are offered even when their channel is switched OFF in settings.
// The signup is accepted and held (`confirm_deferred_at`), and the confirmation
// is sent when an operator turns the channel on — see
// services/status/release-deferred.ts. The old behaviour, a 403 before the row
// was even written, silently discarded a customer's request because of a
// setting they cannot see.
//
// ── NONE SELECTED MEANS ALL ──
// Both pickers default to empty, and empty means "everything". That is stated in
// the hint under each rather than left to be discovered, and it is what the
// fan-out implements (see subscribers.recipientsFor).
//
// FOUR INDEPENDENT ANTI-ABUSE LAYERS, none of which a legitimate visitor can
// see, so that the captcha is never load-bearing (see TurnstileField):
//   1. the server's per-IP rate limit;
//   2. the honeypot below — a real input, off-screen by CLIPPING, never
//      `display: none`, which the better bot frameworks specifically detect;
//   3. the dwell floor: the session records when the form was minted and the
//      submit carries the elapsed ms, so an instant post is rejected;
//   4. mandatory double opt-in — an unconfirmed address is never sent anything.

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { StatusChannel } from '@pcp/shared';

import { BottomSheet } from '../../components/BottomSheet';
import { da } from '../i18n/da';
import {
  StatusApiError,
  postSubscribe,
  postSubscribeResend,
  postSubscribeSession,
  postSubscribeVerify,
  type StatusSubscribeComponent,
  type StatusSubscribeSession,
} from '../lib/statusApi';
import type { SubscribeSheetProps } from '../types';
import { SIcon } from '../ui/SIcon';
import { VisuallyHidden } from '../ui/VisuallyHidden';
import { SystemPicker } from './SystemPicker';
import { TurnstileField } from './TurnstileField';

/** Submit buttons live in the sheet FOOTER, outside the <form> element, so they
 *  stay reachable while the body scrolls. `form=` re-associates them. */
const FORM_ID = 'sp-subscribe-form';
const OTP_FORM_ID = 'sp-subscribe-otp';
const OTP_LENGTH = 6;

type Phase = 'loading' | 'unavailable' | 'form' | 'otp' | 'done';

/** Deliberately permissive: the server is the authority on deliverability, and a
 *  regex that rejects a valid-but-unusual address is a support ticket. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function isPlausiblePhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
}

/** Map a failed request onto the right Danish sentence — never a raw API string.
 *  `field` comes back on a 400 so a visitor who filled in both inputs is told
 *  which one we could not read. */
function messageFor(error: unknown): string {
  if (error instanceof StatusApiError) {
    if (error.status === 429 || error.code === 'rate_limited') return da.subscribeRateLimited;
    if (error.code === 'too_fast' || error.code === 'dwell_too_short') return da.subscribeTooFast;
    if (error.code === 'destination_required') return da.subscribeNeedDestination;
    if (error.code === 'invalid-destination' || error.code === 'invalid_destination') {
      const field = (error as StatusApiError & { field?: string }).field;
      return field === 'phone' ? da.subscribeInvalidPhone : da.subscribeInvalidEmail;
    }
    if (error.code === 'captcha_failed' || error.code === 'captcha_required') return da.captchaPending;
  }
  return da.subscribeError;
}

export function SubscribeSheet({ open, onClose, initialAudienceSlug }: SubscribeSheetProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [session, setSession] = useState<StatusSubscribeSession | null>(null);
  const [openedAt, setOpenedAt] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [audienceIds, setAudienceIds] = useState<string[]>([]);
  const [componentIds, setComponentIds] = useState<string[]>([]);
  const [honeypot, setHoneypot] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaUsable, setCaptchaUsable] = useState(true);
  const [code, setCode] = useState('');
  const [otpChannel, setOtpChannel] = useState<StatusChannel>('sms');
  const [otpDestination, setOtpDestination] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // ── Session ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase('loading');
    setError(null);
    void postSubscribeSession()
      .then((loaded) => {
        if (cancelled) return;
        setSession(loaded);
        setOpenedAt(Date.now());
        setCaptchaToken(null);
        setCaptchaUsable(Boolean(loaded.turnstileSiteKey));
        // PRE-TICK THE AUDIENCE THE VISITOR IS ALREADY LOOKING AT. On /<slug>
        // they have been given a link to their own page, so their own segment
        // is the only sensible default — and asking them to re-pick it from a
        // list of every other customer's is both noise and a nudge to look.
        const match = loaded.audiences.find((audience) => audience.slug === initialAudienceSlug);
        setAudienceIds(match ? [match.id] : []);
        setComponentIds([]);
        setPhase('form');
      })
      .catch(() => {
        if (!cancelled) setPhase('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [open, attempt, initialAudienceSlug]);

  // BottomSheet neither moves focus into the dialog nor restores it on close, so
  // do both here: without it a keyboard visitor is left tabbing through the page
  // BEHIND the sheet, and lands nowhere sensible when it closes.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      const node = restoreRef.current;
      if (node && document.contains(node)) node.focus();
    };
  }, [open]);

  useEffect(() => {
    if (phase === 'form') formRef.current?.focus();
  }, [phase]);

  // ── Derived ─────────────────────────────────────────────────────────
  const audiences = session?.audiences ?? [];
  const captchaRequired = Boolean(session?.turnstileSiteKey) && captchaUsable;
  const minDwellMs = session?.minDwellMs ?? 0;

  const toggleAudience = useCallback((id: string) => {
    setAudienceIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  }, []);


  const handleCaptchaUnavailable = useCallback(() => {
    // The captcha is optional by design — see TurnstileField's header comment.
    setCaptchaUsable(false);
    setCaptchaToken(null);
  }, []);

  // ── Submit ──────────────────────────────────────────────────────────
  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!session || busy) return;
      const emailValue = email.trim();
      const phoneValue = phone.trim();

      // Email is REQUIRED and phone is optional. Notifications are email in all
      // but exceptional cases, so a signup without one is a signup we cannot
      // act on; a number without an address used to be accepted and then sat
      // unusable.
      if (!emailValue) {
        setError(da.subscribeNeedDestination);
        return;
      }
      if (!EMAIL_RE.test(emailValue)) {
        setError(da.subscribeInvalidEmail);
        return;
      }
      if (phoneValue && !isPlausiblePhone(phoneValue)) {
        setError(da.subscribeInvalidPhone);
        return;
      }
      // NO audience/component validation. Empty is a legitimate, and in fact the
      // default, answer on both — it means "everything".
      if (captchaRequired && !captchaToken) {
        setError(da.captchaPending);
        return;
      }
      setBusy(true);
      setError(null);
      try {
        // ── The dwell floor: WAIT it out, do not fail on it ──
        // The server refuses a submit that arrives sooner than minDwellMs after
        // the form token was minted, and it refuses SILENTLY — a 202 that looks
        // exactly like success, so a bot learns nothing from being caught.
        //
        // A human hits that floor too: autofill or a password manager can fill
        // this form in well under two seconds, and then they are told to check
        // an inbox that will never receive anything (b-01550). Telling them off
        // for being quick would be almost as bad, so we simply hold the submit
        // until the floor has passed. It costs at most ~2.5s, once, and it makes
        // the guard incapable of harming a real person.
        //
        // Measured from when the session RESPONSE landed, while the server
        // measures from when it MINTED the token — strictly earlier — so the
        // server always sees at least as much elapsed time as we waited for.
        // Erring in the safe direction, and immune to client/server clock skew.
        const waitFor = minDwellMs - (Date.now() - openedAt);
        if (waitFor > 0) await new Promise((resolve) => setTimeout(resolve, waitFor + 150));
        const dwellMs = Math.max(0, Date.now() - openedAt);
        const result = await postSubscribe({
          ...(emailValue ? { email: emailValue } : {}),
          ...(phoneValue ? { phone: phoneValue } : {}),
          audienceIds,
          componentIds,
          formToken: session.formToken,
          // Wire name is `turnstileToken` (see StatusSubscribeRequest) — the
          // local state is still captchaToken, the SERVER field is not.
          ...(captchaToken ? { turnstileToken: captchaToken } : {}),
          // The honeypot input is named "company" in the DOM (that is the bait a
          // scraper reads); `website` is the field name on the wire.
          website: honeypot,
          dwellMs,
        });

        // The SMS one-time code is the only outcome that needs another step, so
        // it wins the phase even when an email confirmation went out alongside
        // it — the email is waiting in an inbox and needs nothing from us here.
        const sms = result.results.find((r) => r.channel === 'sms');
        // 'otp' used to be listed here as a second alternative; the server has
        // only ever sent 'verify-code', and the shared contract now proves that
        // branch unreachable.
        if (sms && sms.next === 'verify-code') {
          setOtpChannel('sms');
          setOtpDestination(phoneValue);
          setPhase('otp');
          setNotice(da.subscribeSentSms);
          setCode('');
          return;
        }
        setPhase('done');
        setNotice(
          result.results.every((r) => r.next === 'deferred')
            ? da.subscribeDeferred
            : emailValue
              ? da.subscribeSent
              : da.subscribeConfirmed,
        );
      } catch (caught) {
        setError(messageFor(caught));
      } finally {
        setBusy(false);
      }
    },
    [
      audienceIds,
      busy,
      captchaRequired,
      captchaToken,
      componentIds,
      email,
      honeypot,
      minDwellMs,
      openedAt,
      phone,
      session,
    ],
  );

  // ── SMS one-time code ───────────────────────────────────────────────
  const handleVerify = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!session || busy) return;
      if (code.trim().length !== OTP_LENGTH) {
        setError(da.otpInvalid);
        return;
      }
      setBusy(true);
      setError(null);
      try {
        const result = await postSubscribeVerify({
          channel: otpChannel,
          destination: otpDestination,
          code: code.trim(),
          formToken: session.formToken,
        });
        if (result.ok) {
          setPhase('done');
          setNotice(da.subscribeConfirmed);
          return;
        }
        setError(
          result.reason === 'expired'
            ? da.otpExpired
            : result.reason === 'locked'
              ? da.otpLocked
              : result.reason === 'invalid'
                ? da.otpInvalid
                : da.subscribeError,
        );
      } catch (caught) {
        setError(messageFor(caught));
      } finally {
        setBusy(false);
      }
    },
    [busy, code, otpChannel, otpDestination, session],
  );

  const handleResend = useCallback(async () => {
    if (!session || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await postSubscribeResend({
        channel: otpChannel,
        destination: otpDestination,
        formToken: session.formToken,
      });
      if (result.ok) {
        setCode('');
        setNotice(da.subscribeSentSms);
      } else {
        setError(da.subscribeError);
      }
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }, [busy, otpChannel, otpDestination, session]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  // ── Chrome ──────────────────────────────────────────────────────────
  const footer = useMemo(() => {
    if (phase === 'loading') return null;
    if (phase === 'unavailable') {
      return (
        <div className="sp-actions">
          <button type="button" className="sp-btn sp-btn--ghost" onClick={onClose}>
            {da.close}
          </button>
          <button type="button" className="sp-btn sp-btn--primary" onClick={retry}>
            {da.retry}
          </button>
        </div>
      );
    }
    if (phase === 'done') {
      return (
        <div className="sp-actions">
          <button type="button" className="sp-btn sp-btn--primary" onClick={onClose}>
            {da.close}
          </button>
        </div>
      );
    }
    if (phase === 'otp') {
      return (
        <div className="sp-actions">
          <button type="button" className="sp-btn sp-btn--ghost" onClick={() => void handleResend()} disabled={busy}>
            {da.otpResend}
          </button>
          <button type="submit" form={OTP_FORM_ID} className="sp-btn sp-btn--primary" disabled={busy}>
            {busy ? da.subscribeSending : da.otpSubmit}
          </button>
        </div>
      );
    }
    return (
      <div className="sp-actions">
        <button type="button" className="sp-btn sp-btn--ghost" onClick={onClose}>
          {da.close}
        </button>
        <button type="submit" form={FORM_ID} className="sp-btn sp-btn--primary" disabled={busy}>
          {busy ? da.subscribeSending : da.subscribeSubmit}
        </button>
      </div>
    );
  }, [busy, handleResend, onClose, phase, retry]);

  if (!open) return null;

  return (
    <BottomSheet open={open} onClose={onClose} title={da.subscribeTitle} size="full" footer={footer}>
      {phase === 'loading' && (
        <div className="sp-loading" role="status" aria-live="polite">
          <SIcon name="spinner" size={20} className="sp-spin" />
          <span>{da.loading}</span>
        </div>
      )}

      {phase === 'unavailable' && (
        <div className="sp-empty" role="alert">
          <SIcon name="degraded" size={24} />
          <p>{da.subscribeError}</p>
        </div>
      )}

      {phase === 'done' && (
        <div className="sp-success" role="status">
          <SIcon name="operational" size={18} />
          <span>{notice ?? da.subscribeConfirmed}</span>
        </div>
      )}

      {phase === 'otp' && (
        <form id={OTP_FORM_ID} className="sp-form" onSubmit={(event) => void handleVerify(event)}>
          <p className="sp-sheet-intro">{notice ?? da.subscribeSentSms}</p>
          <div className="sp-field">
            <label className="sp-label-text" htmlFor="sp-otp">
              {da.otpPrompt}
            </label>
            <input
              id="sp-otp"
              className="sp-input sp-input--code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={OTP_LENGTH}
              aria-invalid={error ? true : undefined}
              // A one-time code is the one field worth focusing on open: the
              // visitor has just been told to type it and nothing else here matters.
              autoFocus
            />
          </div>
          {error && (
            <p className="sp-error" role="alert">
              <SIcon name="degraded" size={16} />
              {error}
            </p>
          )}
        </form>
      )}

      {phase === 'form' && session && (
        <form
          id={FORM_ID}
          ref={formRef}
          tabIndex={-1}
          className="sp-form"
          onSubmit={(event) => void handleSubmit(event)}
          noValidate
        >
          <p className="sp-sheet-intro">{da.subscribeIntro}</p>

          <div className="sp-field">
            <label className="sp-label-text" htmlFor="sp-email">
              {da.subscribeEmail}
            </label>
            <input
              id="sp-email"
              className="sp-input"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              // The form is noValidate (we own the messages), so `required` here
              // is for assistive tech, not for the browser's own bubble.
              required
              aria-required="true"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>

          <div className="sp-field">
            <label className="sp-label-text" htmlFor="sp-phone">
              {da.subscribePhone}
            </label>
            <input
              id="sp-phone"
              className="sp-input"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              autoCapitalize="off"
              spellCheck={false}
            />
            <p className="sp-help">{da.subscribePhoneHint}</p>
          </div>

          <SystemPicker
            components={session?.components ?? []}
            selected={componentIds}
            onChange={setComponentIds}
            labelId="sp-systems-label"
          />

          {audiences.length > 0 && (
            <div className="sp-field" role="group" aria-labelledby="sp-audiences-label">
              <span className="sp-label-text" id="sp-audiences-label">
                {da.subscribeAudiences}
              </span>
              <p className="sp-help">{da.subscribeAudiencesHint}</p>
              <div className="sp-check-list">
                {audiences.map((audience) => (
                  <label className="sp-check-row" key={audience.id}>
                    <input
                      type="checkbox"
                      checked={audienceIds.includes(audience.id)}
                      onChange={() => toggleAudience(audience.id)}
                    />
                    {audience.name}
                  </label>
                ))}
              </div>
              <p className="sp-help" aria-live="polite">
                {audienceIds.length === 0 ? da.subscribeAudiencesAll : da.subscribeSystemsCount(audienceIds.length)}
              </p>
            </div>
          )}

          {/* Honeypot. Clipped off-screen (never display:none, which the better
              bot frameworks detect), out of the tab order, and hidden from
              assistive tech — a human cannot reach it, so any value is a bot. */}
          <div className="sp-hp" aria-hidden="true">
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(event) => setHoneypot(event.target.value)}
            />
          </div>

          {session.turnstileSiteKey && (
            <TurnstileField
              siteKey={session.turnstileSiteKey}
              onToken={setCaptchaToken}
              onUnavailable={handleCaptchaUnavailable}
            />
          )}

          {error && (
            <p className="sp-error" role="alert">
              <SIcon name="degraded" size={16} />
              {error}
            </p>
          )}
        </form>
      )}
    </BottomSheet>
  );
}
